import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar as CalendarIcon, Clock, MapPin, Users, Loader2 } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { eventsAPI, contractsAPI, standaloneBEOsAPI, eventTypesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const fetchAllEvents = async () => {
    try {
      setLoading(true);
      const [eventsData, contractsData, beosData, etData] = await Promise.all([
        eventsAPI.getAll().catch(() => []),
        contractsAPI.getAll().catch(() => []),
        standaloneBEOsAPI.getAll().catch(() => []),
        eventTypesAPI.getAll().catch(() => [])
      ]);
      setEventTypes(etData);

      // Convert contracts to calendar events
      const contractEvents = (contractsData || []).map(c => ({
        id: `contract-${c.id}`,
        title: c.event_name,
        date: c.event_date,
        time: c.event_time || '',
        location: c.location || '',
        guestCount: c.guest_count || 0,
        client_name: c.client_name || '',
        event_type: c.event_type || 'offsite',
        status: c.status || 'confirmed',
        source: 'contract'
      }));

      // Convert standalone BEOs (not already converted to contracts) to calendar events
      const beoEvents = (beosData || []).filter(b => !b.contract_id).map(b => ({
        id: `beo-${b.id}`,
        title: b.event_name,
        date: b.event_date,
        time: b.event_time || '',
        location: b.location || '',
        guestCount: b.guest_count || 0,
        client_name: b.client_name || '',
        event_type: b.event_type || 'offsite',
        status: b.status || 'draft',
        source: 'beo'
      }));

      // Merge all sources, deduplicate by date+title
      const allEvents = [...(eventsData || []), ...contractEvents, ...beoEvents];
      setEvents(allEvents);
    } catch (error) {
      toast({ title: 'Error loading events', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const eventsOnSelectedDate = events.filter(event => {
    const eventDate = parseDate(event.date);
    return eventDate && eventDate.toDateString() === selectedDate.toDateString();
  });

  const eventDates = events.map(e => parseDate(e.date)).filter(Boolean);

  // Group dates by event type for color-coded calendar
  const eventsByType = {};
  events.forEach(e => {
    const d = parseDate(e.date);
    if (!d) return;
    const type = e.event_type || 'other';
    if (!eventsByType[type]) eventsByType[type] = [];
    eventsByType[type].push(d);
  });

  const modifiers = { hasEvent: eventDates };
  const modifiersStyles = {};
  const typeColors = {
    onsite: '#9333ea',
    offsite: '#dc2626',
    tournament: '#16a34a',
    dropoff: '#2563eb',
    cocktail_coach: '#0abab5',
  };
  Object.entries(eventsByType).forEach(([type, dates]) => {
    const key = `type_${type}`;
    modifiers[key] = dates;
    modifiersStyles[key] = {
      fontWeight: 'bold',
      backgroundColor: typeColors[type] || '#6b7280',
      color: '#ffffff',
      borderRadius: '50%'
    };
  });

  const getEventColor = (eventType) => {
    const meta = eventTypes.find(t => t.slug === eventType);
    if (meta) return meta.color;
    // Fallback hardcoded colours for environments where API hasn't seeded yet
    switch (eventType) {
      case 'onsite': return '#9333ea';
      case 'offsite': return '#dc2626';
      case 'tournament': return '#16a34a';
      case 'dropoff': return '#1e3a5f';
      case 'cocktail_coach': return '#0abab5';
      case 'pickup': return '#1e3a5f';
      default: return '#6b7280';
    }
  };

  const getSourceBadge = (source) => {
    if (source === 'contract') return { label: 'Contract', bg: 'bg-green-100 text-green-800' };
    if (source === 'beo') return { label: 'BEO', bg: 'bg-purple-100 text-purple-800' };
    return { label: 'Event', bg: 'bg-blue-100 text-blue-800' };
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="calendar-page">
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Event Calendar</h1>
        <p className="text-gray-600">View and manage your scheduled events</p>
        <div className="flex flex-wrap gap-4 mt-4">
          {(eventTypes.length > 0
            ? eventTypes.map(t => ({ color: t.color, label: t.name }))
            : [
                { color: '#9333ea', label: 'Onsite' }, { color: '#dc2626', label: 'Offsite' },
                { color: '#16a34a', label: 'Tournaments' },
                { color: '#0abab5', label: 'Cocktail Coach' },
                { color: '#1e3a5f', label: 'Pick Up' },
                { color: '#1e3a5f', label: 'Drop Off' }
              ]
          ).map(item => (
            <div key={item.label} className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-none shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-2">
              <CalendarIcon size={20} /><span>Select Date</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={modifiers}
              modifiersStyles={modifiersStyles}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-none shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white rounded-t-lg">
            <CardTitle>Events on {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {eventsOnSelectedDate.length > 0 ? (
              <div className="space-y-4">
                {eventsOnSelectedDate.map((event) => {
                  const badge = getSourceBadge(event.source);
                  return (
                    <div key={event.id} className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                      style={{ borderLeft: `6px solid ${getEventColor(event.event_type)}` }}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-[#1e3a5f] mb-1">{event.title}</h3>
                          <p className="text-sm text-gray-600">{event.client_name}</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg}`}>{badge.label}</span>
                          <span className="px-4 py-2 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: getEventColor(event.event_type) }}>
                            {event.event_type?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {event.time && (
                          <div className="flex items-center space-x-3 text-gray-600">
                            <Clock size={18} className="text-[#1e3a5f]" /><span className="text-sm">{event.time}</span>
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center space-x-3 text-gray-600">
                            <MapPin size={18} className="text-[#1e3a5f]" /><span className="text-sm">{event.location}</span>
                          </div>
                        )}
                        {event.guestCount > 0 && (
                          <div className="flex items-center space-x-3 text-gray-600">
                            <Users size={18} className="text-[#1e3a5f]" /><span className="text-sm">{event.guestCount} guests</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No events scheduled for this date</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 border-none shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
          <CardTitle>All Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {events.filter(e => { const d = parseDate(e.date); return d && d >= new Date(new Date().setHours(0,0,0,0)); }).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No upcoming events</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events
                .filter(e => { const d = parseDate(e.date); return d && d >= new Date(new Date().setHours(0,0,0,0)); })
                .sort((a, b) => parseDate(a.date) - parseDate(b.date))
                .map((event) => {
                  const badge = getSourceBadge(event.source);
                  return (
                    <div key={event.id} className="p-4 bg-white rounded-lg border border-gray-200 hover:border-[#c9a227] hover:shadow-md transition-all">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-[#1e3a5f]">{event.title}</h4>
                        <div className="flex gap-1">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg}`}>{badge.label}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            event.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>{event.status}</span>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <CalendarIcon size={14} />
                          <span>{parseDate(event.date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        </div>
                        {event.client_name && <p className="text-xs text-gray-500">{event.client_name}</p>}
                        {event.guestCount > 0 && (
                          <div className="flex items-center space-x-2">
                            <Users size={14} /><span>{event.guestCount} guests</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
